<template>
  <a-drawer
    width="35%"
    :label-col="4"
    :wrapper-col="14"
    :visible="open"
    @close="onClose"
    :destroyOnClose="true">
    <a-divider orientation="left">
      <b>{{ formTitle }}</b>
    </a-divider>
    <a-form-model ref="form" :model="form" :rules="rules">
      <a-form-model-item :label="$t('views.systemMenu.form.parentMenu')" prop="parentId">
        <a-tree-select
          v-model="form.parentId"
          style="width: 100%"
          :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
          :tree-data="menuOptions"
          :placeholder="$t('views.systemMenu.form.placeholderSelect')"
          :replaceFields="{ children: 'children', title: 'menuName', key: 'id', value: 'id' }"
          tree-default-expand-all>
        </a-tree-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemMenu.form.menuType')" prop="menuType">
        <a-radio-group v-model="form.menuType" button-style="solid">
          <a-radio-button value="M">{{ $t('views.systemMenu.form.menuType.dir') }}</a-radio-button>
          <a-radio-button value="C">{{ $t('views.systemMenu.form.menuType.menu') }}</a-radio-button>
          <a-radio-button value="F">{{ $t('views.systemMenu.form.menuType.button') }}</a-radio-button>
        </a-radio-group>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemMenu.form.icon')" prop="icon" v-if="form.menuType != 'F'">
        <a-space size="large">
          <a-icon :component="allIcon[form.icon + 'Icon']" v-if="form.icon && allIcon[form.icon + 'Icon']" />
          <a-icon :type="form.icon" v-if="form.icon && !allIcon[form.icon + 'Icon']" />
          <a-button type="dashed" @click="selectIcon">
            {{ $t('views.systemMenu.form.selectIcon') }}
          </a-button>
          <a @click="cancelSelectIcon" v-if="iconVisible" style="margin-left: 8px">
            {{ $t('views.systemMenu.form.collapseIcon') }}
            <a-icon type="up" />
          </a>
        </a-space>
      </a-form-model-item>
      <a-card :body-style="{ padding: '10px 20px' }" :bordered="false" v-if="iconVisible">
        <icon-selector v-model="form.icon" @change="handleIconChange" :svgIcons="iconList" :allIcon="allIcon" />
      </a-card>
      <a-form-model-item :label="$t('views.systemMenu.form.menuName')" prop="menuName">
        <a-input v-model="form.menuName" :placeholder="$t('views.systemMenu.form.placeholderInput')" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.systemMenu.form.orderNum')" prop="orderNum">
        <a-input-number v-model="form.orderNum" :min="0" :max="9999" style="width: 100%" />
      </a-form-model-item>
      <a-form-model-item prop="isFrame" v-if="form.menuType != 'F'">
        <span slot="label">
          {{ $t('views.systemMenu.form.isFrame') }}
          <a-tooltip>
            <template slot="title">
              {{ $t('views.systemMenu.form.isFrameTooltip') }}
            </template>
            <a-icon type="question-circle-o" />
          </a-tooltip>
        </span>
        <a-radio-group v-model="form.isFrame" button-style="solid">
          <a-radio-button v-for="(d, index) in isFrameOptions" :key="index" :value="parseInt(d.value)">{{ d.label }}</a-radio-button>
        </a-radio-group>
      </a-form-model-item>
      <a-form-model-item prop="path" v-if="form.menuType != 'F'">
        <span slot="label">
          {{ $t('views.systemMenu.form.path') }}
          <a-tooltip>
            <template slot="title">
              {{ $t('views.systemMenu.form.pathTooltip') }}
            </template>
            <a-icon type="question-circle-o" />
          </a-tooltip>
        </span>
        <a-input v-model="form.path" :placeholder="$t('views.systemMenu.form.placeholderInput')" />
      </a-form-model-item>
      <a-form-model-item prop="component" v-if="form.menuType == 'C'">
        <span slot="label">
          {{ $t('views.systemMenu.form.component') }}
          <a-tooltip>
            <template slot="title">
              {{ $t('views.systemMenu.form.componentTooltip') }}
            </template>
            <a-icon type="question-circle-o" />
          </a-tooltip>
        </span>
        <a-input v-model="form.component" :placeholder="$t('views.systemMenu.form.placeholderInput')" />
      </a-form-model-item>
      <a-form-model-item prop="perms" v-if="form.menuType != 'M'">
        <span slot="label">
          {{ $t('views.systemMenu.form.perms') }}
          <a-tooltip>
            <template slot="title">
              {{ $t('views.systemMenu.form.permsTooltip') }}
            </template>
            <a-icon type="question-circle-o" />
          </a-tooltip>
        </span>
        <a-input v-model="form.perms" :placeholder="$t('views.systemMenu.form.placeholderInput')" :maxLength="100" />
      </a-form-model-item>
      <a-form-model-item prop="visible" v-if="form.menuType != 'F'">
        <span slot="label">
          {{ $t('views.systemMenu.form.visible') }}
          <a-tooltip>
            <template slot="title">
              {{ $t('views.systemMenu.form.visibleTooltip') }}
            </template>
            <a-icon type="question-circle-o" />
          </a-tooltip>
        </span>
        <a-radio-group v-model="form.visible" button-style="solid">
          <a-radio-button v-for="(d, index) in localizedVisibleOptions" :key="index" :value="parseInt(d.value)">{{ d.label }}</a-radio-button>
        </a-radio-group>
      </a-form-model-item>
      <a-form-model-item prop="status" v-if="form.menuType != 'F'">
        <span slot="label">
          {{ $t('views.systemMenu.form.status') }}
          <a-tooltip>
            <template slot="title">
              {{ $t('views.systemMenu.form.statusTooltip') }}
            </template>
            <a-icon type="question-circle-o" />
          </a-tooltip>
        </span>
        <a-radio-group v-model="form.status" button-style="solid">
          <a-radio-button v-for="(d, index) in localizedStatusOptions" :key="index" :value="parseInt(d.value)">{{ d.label }}</a-radio-button>
        </a-radio-group>
      </a-form-model-item>
      <a-form-model-item prop="isCache" v-if="form.menuType == 'C'">
        <span slot="label">
          {{ $t('views.systemMenu.form.isCache') }}
          <a-tooltip>
            <template slot="title">
              {{ $t('views.systemMenu.form.isCacheTooltip') }}
            </template>
            <a-icon type="question-circle-o" />
          </a-tooltip>
        </span>
        <a-radio-group v-model="form.isCache" button-style="solid">
          <a-radio-button v-for="(d, index) in localizedStatusOptions" :key="index" :value="parseInt(d.value)">{{ d.label }}</a-radio-button>
        </a-radio-group>
      </a-form-model-item>
      <div class="bottom-control">
        <a-space>
          <a-button type="primary" :loading="submitLoading" @click="submitForm">
            {{ $t('views.systemMenu.form.save') }}
          </a-button>
          <a-button type="dashed" @click="cancel">
            {{ $t('views.systemMenu.form.cancel') }}
          </a-button>
        </a-space>
      </div>
    </a-form-model>
  </a-drawer>
</template>

<script>

import { getMenu, addMenu, updateMenu } from '@/api/system/menu'
import allIcon from '@/core/icons'
import icons from '@/utils/requireIcons'
import IconSelector from '@/components/IconSelector'

export default {
  name: 'CreateForm',
  props: {
    statusOptions: {
      type: Array,
      required: true
    },
    visibleOptions: {
      type: Array,
      required: true
    },
    menuOptions: {
      type: Array,
      required: true
    }
  },
  components: {
    IconSelector
  },
  data() {
    return {
      allIcon,
      iconVisible: false,
      iconList: icons,
      submitLoading: false,
      formTitle: '',
      form: {
        id: undefined,
        parentId: '0',
        menuName: undefined,
        icon: undefined,
        menuType: 'M',
        orderNum: undefined,
        isFrame: 0,
        isCache: 1,
        visible: 1,
        status: 1
      },
      open: false
    }
  },
  computed: {
    isFrameOptions () {
      return [
        { value: 1, label: this.$t('views.systemMenu.dict.yesNo.1') },
        { value: 0, label: this.$t('views.systemMenu.dict.yesNo.0') }
      ]
    },
    localizedStatusOptions () {
      return this.localizeDictOptions(this.statusOptions, 'views.systemMenu.dict.status')
    },
    localizedVisibleOptions () {
      return this.localizeDictOptions(this.visibleOptions, 'views.systemMenu.dict.visible')
    },
    rules () {
      return {
        menuName: [{ required: true, message: this.$t('views.systemMenu.form.rules.menuNameRequired'), trigger: 'blur' }],
        orderNum: [{ required: true, message: this.$t('views.systemMenu.form.rules.orderNumRequired'), trigger: 'blur' }],
        path: [{ required: true, message: this.$t('views.systemMenu.form.rules.pathRequired'), trigger: 'blur' }],
        component: [{ required: true, message: this.$t('views.systemMenu.form.rules.componentRequired'), trigger: 'blur' }]
      }
    }
  },
  methods: {
    localizeDictOptions (options, keyPrefix) {
      return (options || []).map(item => {
        const i18nKey = `${keyPrefix}.${item.value}`
        return {
          ...item,
          label: this.$te(i18nKey) ? this.$t(i18nKey) : item.label
        }
      })
    },
    onClose() {
      this.open = false
      this.iconVisible = false
    },
    cancel() {
      this.open = false
      this.iconVisible = false
      this.reset()
    },
    reset() {
      this.form = {
        id: undefined,
        parentId: '0',
        menuName: undefined,
        icon: undefined,
        menuType: 'M',
        orderNum: undefined,
        isFrame: 0,
        isCache: 1,
        visible: 1,
        status: 1
      }
    },
    handleAdd(row) {
      this.reset()
      this.$emit('select-tree')
      if (row != null && row.id) {
        this.form.parentId = row.id
      } else {
        this.form.parentId = '0'
      }
      this.open = true
      this.formTitle = this.$t('views.systemMenu.form.titleAdd')
    },
    handleUpdate(row) {
      this.reset()
      this.$emit('select-tree')
      getMenu(row.id).then(response => {
        this.form = response.data
        this.open = true
        this.formTitle = this.$t('views.systemMenu.form.titleEdit')
      })
    },
    submitForm: function () {
      this.$refs.form.validate(valid => {
        if (valid) {
          this.submitLoading = true
          if (this.form.id !== undefined) {
            updateMenu(this.form).then(() => {
              this.$message.success(this.$t('views.systemMenu.msg.updateSuccess'), 3)
              this.open = false
              this.iconVisible = false
              this.$emit('ok')
            }).finally(() => {
              this.submitLoading = false
            })
          } else {
            addMenu(this.form).then(() => {
              this.$message.success(this.$t('views.systemMenu.msg.addSuccess'), 3)
              this.open = false
              this.iconVisible = false
              this.$emit('ok')
            }).finally(() => {
              this.submitLoading = false
            })
          }
        } else {
          return false
        }
      })
    },
    handleIconChange(icon) {
      this.iconVisible = false
      this.form.icon = icon
    },
    selectIcon() {
      this.iconVisible = !this.iconVisible
    },
    cancelSelectIcon() {
      this.iconVisible = false
    }
  }
}
</script>
